import React, { useState, useRef, useEffect } from "react";
import { MoveRight } from "lucide-react";
import { CardProps } from "./CardProps";

const Card: React.FC<CardProps> = ({
  img,
  title,
  text_alignment,
  paragraph,
  link,
  linkText,
  externalLink,
  width,
  height,
  title_text_size,
  paragraph_text_size,
  link_text_size,
  tooltip,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<"left" | "right">(
    "right"
  );
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePosition = () => {
      if (tooltipRef.current) {
        const rect = tooltipRef.current.getBoundingClientRect();
        if (rect.right + 300 > window.innerWidth) {
          setTooltipPosition("left");
        } else {
          setTooltipPosition("right");
        }
      }
    };

    handlePosition();
    window.addEventListener("resize", handlePosition);

    return () => {
      window.removeEventListener("resize", handlePosition);
    };
  }, [tooltip]);

  return (
    <React.Fragment>
      <div
        className="relative transition-all duration-300 ease-linear card hover:-translate-y-2 dark:bg-zink-600"
        onMouseEnter={() => tooltip && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        ref={tooltipRef}
        style={{ zIndex: showTooltip ? 10 : 1 }}
      >
        <div className="p-3 flex flex-col justify-center items-center">
          {img && (
            externalLink ? 
            <a href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                width={width ?? -1}
                height={height ?? -1}
                src={img}
                alt=""
                className="rounded-md shadow"
              />
            </a>
            :
            <a href={link}>
              <img
                width={width ?? -1}
                height={height ?? -1}
                src={img}
                alt=""
                className="rounded-md shadow"
              />
            </a>
          )}
          <div className="mt-3">
            {title && (
              <h6
                className={`mt-3 mb-2 ${
                  title_text_size ??
                  "text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl 7xl:text-7xl 8xl:text-8xl 9xl:text-9xl"
                } ${text_alignment}`}
              >
                {
                  externalLink ?
                  <a 
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer">
                    {title}
                  </a>
                  :
                  <a href={link}>{title}</a>
                }
              </h6>
            )}
            {!tooltip && paragraph && (
              <p
                className={`mb-3 text-slate-500 dark:text-zink-200 text-16 text-justify ${
                  paragraph_text_size ??
                  "text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl 7xl:text-7xl 8xl:text-8xl 9xl:text-9xl"
                }`}
              >
                {paragraph}
              </p>
            )}
            {linkText && (
                externalLink ? 
                  <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-custom-500 ${
                    link_text_size ??
                    "text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl 7xl:text-7xl 8xl:text-8xl 9xl:text-9xl"
                  }`}
                >
                  {linkText}{" "}
                  <MoveRight className="inline-block w-4 h-4 align-middle ltr:ml-1 rtl:mr-1 rtl:rotate-180" />
                </a>
                :
                <a
                  href={link}
                  className={`text-custom-500 ${
                    link_text_size ??
                    "text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl 7xl:text-7xl 8xl:text-8xl 9xl:text-9xl"
                  }`}
                >
                  {linkText}{" "}
                  <MoveRight className="inline-block w-4 h-4 align-middle ltr:ml-1 rtl:mr-1 rtl:rotate-180" />
                </a>
            )}
          </div>
        </div>
        {tooltip && showTooltip && paragraph && (
          <div
            className={`absolute p-4 bg-gray-700 text-white text-lg rounded shadow-lg ${
              tooltipPosition === "right" ? "left-full ml-4" : "right-full mr-4"
            }`}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              maxWidth: "400px", // Larger width for bigger tooltip
              whiteSpace: "normal", // Allow wrapping of text
            }}
          >
            {paragraph}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Card;
